import { cardEditContext } from '@context/card-edit-context';
import { Box, type BoxProps, Inline, Text } from '@ui-lib';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { useContext, useMemo } from 'react';
import { IconCubeSvg } from 'ui-lib/src/lib/components/Icons/IconCube/IconCubeSvg';
import { IconImageSvg } from 'ui-lib/src/lib/components/Icons/IconImage/IconImageSvg';
import { IconUserSvg } from 'ui-lib/src/lib/components/Icons/IconUser/IconUserSvg';

import type { ImageType, PlaceholderImageSize } from '../../types';
import { toFriendlyName } from '../../utilities';
import * as styles from './TemplatePlaceholder.css';

type TemplatePlaceholderProps = {
  type: ImageType;
  showText?: boolean;
  isOptional?: Maybe<boolean>;
  /** Use position absolute when the placeholder is inside the Banner display box to make sure it's full height */
  position?: 'absolute' | 'relative';
  placeholderImageSize: PlaceholderImageSize;
  url?: string | null;
  /** The placeholder images are used in multiple different places in the dashboard. inCardHeader indicates whether these placeholders are being rendered in the CardHeader component or not */
  inCardHeader?: boolean;
};

export const TemplatePlaceholder: React.FC<TemplatePlaceholderProps> = ({
  type,
  showText,
  isOptional,
  position = 'relative',
  placeholderImageSize = 'normal',
  url,
  inCardHeader,
}) => {
  const { showRequiredAttributesErrors } = useContext(cardEditContext);

  /** Select the right icon depending on the image type */
  const icon = useMemo(() => {
    switch (type) {
      case 'cover':
        return <IconImageSvg className={styles.icon[placeholderImageSize]} />;
      case 'profile':
        return <IconUserSvg className={styles.icon[placeholderImageSize]} />;
      case 'logo':
      default:
        return <IconCubeSvg className={styles.icon[placeholderImageSize]} />;
    }
  }, [placeholderImageSize, type]);

  const positionStyles = useMemo((): BoxProps => {
    switch (position) {
      case 'absolute':
        return {
          position: 'absolute',
          top: 0,
          left: 0,
        };
      case 'relative':
      default:
        return {
          position: 'relative',
        };
    }
  }, [position]);

  return (
    <Box
      width="full"
      height="full"
      className={
        showRequiredAttributesErrors && !url && inCardHeader && !isOptional
          ? styles.imagePlaceholderRequiredError
          : styles.imagePlaceholder
      }
      display="flex"
      alignItems="center"
      justifyContent="center"
      {...positionStyles}
    >
      <Box
        style={{
          containerType: 'inline-size',
        }}
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="center"
        gap="small"
        width="full"
      >
        {icon}
        {showText ? (
          <Text align="center" className={styles.templatePlaceholderText} size="large">
            {toFriendlyName(type)}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
};
